import { useMatches } from '@tanstack/react-router';
import { ChevronLeft, GripVertical } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useCasePartnerShell } from '@/components/contexts/CasePartnerContext';
import { useNavigation } from '@/components/contexts/NavigationContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import {
  CASE_PARTNER_HISTORY_WIDTH,
  getSidePanelOverlayThreshold,
  NAV_SIDEBAR_WIDTH_COLLAPSED,
  NAV_SIDEBAR_WIDTH_DIFF,
  NAV_SIDEBAR_WIDTH_EXPANDED,
  shouldCollapseNavForSidePanel,
} from '@/lib/nav-constants';
import { GlobalSidePanel } from './GlobalSidePanel';

const MIN_PANEL_WIDTH = 300;

const PANEL_NAV_MARGIN = 10;
const AUTO_CLOSE_PANEL_ROUTES = new Set<string>([
  '/analytics',
  '/admin',
  '/admin/tenants',
  '/matter/$slug/settings',
  '/matter/$slug/members',
  '/matter/$slug/sync',
  '/home',
  '/all-matters',
  '/settings/account',
]);
const MATTER_ROUTE = '/matter/$slug';
const OVERVIEW_ROUTE = '/matter/$slug/overview';
const HIDDEN_PANEL_ROUTE = new Set<string>([...AUTO_CLOSE_PANEL_ROUTES, OVERVIEW_ROUTE]);
const CASE_PARTNER_TAB_ID = 'case-partner';

const anyMatches = (matches: ReadonlyArray<{ fullPath: string }>, paths: Set<string>) =>
  matches.some((m) => paths.has(m.fullPath));

// Maximum panel width — leave a small gap between the panel and the nav sidebar's right edge
const getAbsoluteMaxWidth = (navIsCollapsed: boolean) =>
  window.innerWidth - (navIsCollapsed ? NAV_SIDEBAR_WIDTH_COLLAPSED : NAV_SIDEBAR_WIDTH_EXPANDED) - PANEL_NAV_MARGIN;

export const ResizableSidePanel: React.FC = () => {
  const { isOpen, openPanel, tabs, setPanelWidth, closePanel } = useSidePanel();
  const { collapsed: navCollapsed, setCollapsed: setNavCollapsed } = useNavigation();
  const { isChatHistoryDocked } = useCasePartnerShell();
  const reservedContentWidth = isChatHistoryDocked ? CASE_PARTNER_HISTORY_WIDTH : 0;
  const matches = useMatches();
  // Homepage can open a document in the panel when given an explicit matterGuid.
  // Other off-matter routes still skip rendering until the panel is actually open.
  const onMatterRoute = matches.some((m) => m.fullPath === MATTER_ROUTE);
  const shouldHideOnRoute = anyMatches(matches, HIDDEN_PANEL_ROUTE);

  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const resizeRafIdRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  const lastWidthRef = useRef<number>(window.innerWidth * 0.4); // Remember last width
  const navCollapsedRef = useRef(navCollapsed); // Ref to avoid closure issues
  const reservedContentWidthRef = useRef(reservedContentWidth);
  const setPanelWidthRef = useRef(setPanelWidth); // Ref to avoid closure issues
  const closePanelRef = useRef(closePanel); // Ref to avoid effect churn on function identity changes

  // Keep refs in sync with latest values
  useEffect(() => {
    navCollapsedRef.current = navCollapsed;
    reservedContentWidthRef.current = reservedContentWidth;
    setPanelWidthRef.current = setPanelWidth;
    closePanelRef.current = closePanel;
  }, [navCollapsed, reservedContentWidth, setPanelWidth, closePanel]);

  const autoCollapsedNavRef = useRef(false);
  const wasOpenRef = useRef(isOpen);
  const wasNavCollapsedRef = useRef(navCollapsed);

  // Collapse the left nav when the panel opens on a narrow screen, and restore
  // it when the panel closes — unless the user changed the nav themselves.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    const wasNavCollapsed = wasNavCollapsedRef.current;
    wasOpenRef.current = isOpen;
    wasNavCollapsedRef.current = navCollapsed;

    if (isOpen && wasNavCollapsed && !navCollapsed) {
      autoCollapsedNavRef.current = false;
    }

    if (isOpen && !wasOpen) {
      if (!navCollapsed && shouldCollapseNavForSidePanel(window.innerWidth, lastWidthRef.current, reservedContentWidth)) {
        autoCollapsedNavRef.current = true;
        setNavCollapsed(true);
      }
      return;
    }

    if (!isOpen && wasOpen && autoCollapsedNavRef.current) {
      autoCollapsedNavRef.current = false;
      setNavCollapsed(false);
    }
  }, [isOpen, navCollapsed, setNavCollapsed]);

  // Track previous navCollapsed state to detect actual changes
  const prevNavCollapsedRef = useRef(navCollapsed);

  // Adjust panel width when sidebar collapses/expands
  useEffect(() => {
    // Only adjust if navCollapsed actually changed and panel is open
    if (!isOpen || !panelRef.current || prevNavCollapsedRef.current === navCollapsed) {
      prevNavCollapsedRef.current = navCollapsed;
      return;
    }

    prevNavCollapsedRef.current = navCollapsed;

    // Auto-collapse exists to free space for the page, not to grow the panel.
    if (autoCollapsedNavRef.current && navCollapsed) {
      return;
    }

    const sidebarWidthDiff = NAV_SIDEBAR_WIDTH_DIFF;
    const overlayThreshold = getSidePanelOverlayThreshold(navCollapsed, reservedContentWidth);
    const absoluteMaxWidth = getAbsoluteMaxWidth(navCollapsed);

    let newWidth: number;
    if (lastWidthRef.current > overlayThreshold) {
      // Already in overlay territory — keep width, just clamp to new absolute max
      newWidth = Math.min(absoluteMaxWidth, lastWidthRef.current);
    } else if (navCollapsed) {
      // Sidebar collapsed - try to expand by the diff, cap at overlay threshold
      newWidth = Math.min(overlayThreshold, lastWidthRef.current + sidebarWidthDiff);
    } else {
      // Sidebar expanded - shrink by the diff, respect min width
      newWidth = Math.max(MIN_PANEL_WIDTH, lastWidthRef.current - sidebarWidthDiff);
    }

    panelRef.current.style.width = `${newWidth}px`;
    lastWidthRef.current = newWidth;
    setPanelWidth(newWidth);
  }, [navCollapsed, reservedContentWidth, isOpen, setPanelWidth]);

  const finishDrag = useCallback(() => {
    if (!isDraggingRef.current) return;

    const pointerId = activePointerIdRef.current;
    const handle = dragHandleRef.current;
    if (pointerId !== null && handle?.hasPointerCapture?.(pointerId)) {
      try {
        handle.releasePointerCapture(pointerId);
      } catch {
        // Pointer capture may already be released by the browser.
      }
    }

    isDraggingRef.current = false;
    activePointerIdRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (panelRef.current) {
      panelRef.current.style.transition = '';
    }

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // Handle drag start
  const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;

    e.preventDefault();
    isDraggingRef.current = true;
    activePointerIdRef.current = e.pointerId;
    startXRef.current = e.clientX;
    e.currentTarget.setPointerCapture?.(e.pointerId);

    if (panelRef.current) {
      startWidthRef.current = panelRef.current.offsetWidth;
      // Disable transition during drag for smooth resize
      panelRef.current.style.transition = 'none';
    }

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle drag move and drag end
  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current || !panelRef.current) return;
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;

      // Cancel previous frame if it hasn't executed yet
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Use requestAnimationFrame for smooth 60fps updates
      rafIdRef.current = requestAnimationFrame(() => {
        if (!panelRef.current) return;

        // Recalculate max width on each frame to account for window resize or nav changes
        const absoluteMaxWidth = getAbsoluteMaxWidth(navCollapsedRef.current);

        const deltaX = startXRef.current - e.clientX;
        const newWidth = Math.max(MIN_PANEL_WIDTH, Math.min(absoluteMaxWidth, startWidthRef.current + deltaX));

        panelRef.current.style.width = `${newWidth}px`;
        lastWidthRef.current = newWidth;
        setPanelWidthRef.current(newWidth);
      });
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;
      finishDrag();
    };

    const handleWindowResize = () => {
      // Skip during active drag — the drag handler already uses live window.innerWidth
      if (isDraggingRef.current || !panelRef.current) return;

      if (resizeRafIdRef.current !== null) {
        cancelAnimationFrame(resizeRafIdRef.current);
      }

      resizeRafIdRef.current = requestAnimationFrame(() => {
        if (!panelRef.current) return;

        const overlayThreshold = getSidePanelOverlayThreshold(
          navCollapsedRef.current,
          reservedContentWidthRef.current,
        );
        const absoluteMaxWidth = getAbsoluteMaxWidth(navCollapsedRef.current);
        const inOverlayMode = lastWidthRef.current > overlayThreshold;
        const maxWidth = inOverlayMode ? absoluteMaxWidth : overlayThreshold;
        const newWidth = Math.min(Math.max(MIN_PANEL_WIDTH, lastWidthRef.current), maxWidth);

        panelRef.current.style.width = `${newWidth}px`;
        lastWidthRef.current = newWidth;
        setPanelWidthRef.current(newWidth);

        resizeRafIdRef.current = null;
      });
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
    window.addEventListener('blur', finishDrag);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
      window.removeEventListener('blur', finishDrag);
      window.removeEventListener('resize', handleWindowResize);
      finishDrag();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (resizeRafIdRef.current !== null) {
        cancelAnimationFrame(resizeRafIdRef.current);
      }
    };
  }, [finishDrag]);

  // Set width when panel opens (restore last width or use default)
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const overlayThreshold = getSidePanelOverlayThreshold(navCollapsed, reservedContentWidth);
      const absoluteMaxWidth = getAbsoluteMaxWidth(navCollapsed);
      const inOverlayMode = lastWidthRef.current > overlayThreshold;
      const maxWidth = inOverlayMode ? absoluteMaxWidth : overlayThreshold;

      const constrainedWidth = Math.min(Math.max(MIN_PANEL_WIDTH, lastWidthRef.current), maxWidth);

      panelRef.current.style.width = `${constrainedWidth}px`;
      lastWidthRef.current = constrainedWidth;
      setPanelWidth(constrainedWidth);
    }
  }, [isOpen, setPanelWidth, navCollapsed, reservedContentWidth]);

  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Close on navigation to these routes but not on programmatic opens (e.g. upload manager links).
  // isOpen is read via ref so it's not a dependency — the effect must only fire on route changes.
  // Depends on the leaf route path (not the derived boolean) so it fires even when navigating between
  // two auto-close routes (e.g. settings → home), where the boolean would stay true and not change.
  const leafRoutePath = matches[matches.length - 1]?.fullPath;
  useEffect(() => {
    if (leafRoutePath && AUTO_CLOSE_PANEL_ROUTES.has(leafRoutePath) && isOpenRef.current) {
      closePanelRef.current();
    }
  }, [leafRoutePath]);

  // Off a matter route document viewers used to throw because they read
  // `/matter/$slug` from the URL. Homepage can still open a document when the
  // viewer is given an explicit matterGuid, so allow the panel while it is open.
  if (!onMatterRoute && !isOpen) {
    return null;
  }

  // When panel is closed, conditionally show expand button
  if (!isOpen) {
    const isOverviewPage = leafRoutePath === OVERVIEW_ROUTE;

    if (isOverviewPage) {
      const hasCasePartnerTab = tabs.some(tab => tab.id === CASE_PARTNER_TAB_ID);
      const documentTab = tabs.find(tab => tab.id === 'document');
      const hasValidDocument = documentTab &&
        !documentTab.disabled &&
        documentTab.content &&
        typeof documentTab.content === 'object' &&
        'props' in documentTab.content &&
        (documentTab.content.props as { documentGuid?: string })?.documentGuid;

      if (!hasValidDocument && !hasCasePartnerTab) {
        return null;
      }
    } else if (shouldHideOnRoute) {
      return null;
    }

    if (tabs.length === 0) {
      return null;
    }

    return (
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40">
        <button
          type="button"
          onClick={() => {
            openPanel(tabs);
          }}
          className="bg-white border border-l-0 border-slate-200 rounded-l-md p-2 shadow-lg transition-colors hover:bg-slate-50 cursor-pointer"
          aria-label="Expand panel"
          title="Expand panel"
        >
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="fixed right-0 top-0 bottom-0 flex z-30 pointer-events-none">
      {/* Drag handle */}
      <div
        ref={dragHandleRef}
        onPointerDown={handleDragStart}
        onLostPointerCapture={finishDrag}
        className="w-2 -ml-1 cursor-col-resize flex items-center justify-center relative group pointer-events-auto touch-none"
      >
        <div className="absolute inset-y-0 right-0 w-1 bg-stone-200 transition-colors group-hover:bg-blue-200" />
        <div className="flex h-4 w-3 items-center justify-center rounded-xs border border-stone-200 bg-stone-200 group-hover:bg-blue-200 z-10">
          <GripVertical className="h-2.5 w-2.5" />
        </div>
      </div>

      {/* Panel content */}
      <div ref={panelRef} className="h-full transition-all pointer-events-auto">
        <GlobalSidePanel />
      </div>
    </div>
  );
};
